import localforage from "localforage";
import { VERSION, STORE_NAME } from "./FileStorage";

// ~ 5MiB
export const LARGE_STORE_FILE_SIZE_LIMIT = 5 * 1000 * 1000;

// prefix file text, which marks presence of a large file
export const LARGE_FILE_MARKER =
    "qalam-Da9OhthaeF3naez3uwooFieKai5jahseishooch9";

type GetResolveFn = ({ path, value }: { path: string; value: string }) => void;
type SetResolveFn = ({ path }: { path: string }) => void;
type NextPartFn = (counter: number) => void;

export class LargeFileStorage {
    private instance: LocalForage;

    constructor(private id: string) {
        this.instance = localforage.createInstance({
            name: this.name(),
            storeName: STORE_NAME,
            version: VERSION,
            driver: [
                localforage.INDEXEDDB,
                localforage.WEBSQL,
                localforage.LOCALSTORAGE,
            ],
        });
    }

    name() {
        return `ur_l_${this.id}`;
    }

    destroy() {
        // since IndexedDB is the first DB in driver list, so we are using it's
        // direct API to delete underlying storage otherwise we would not do this
        const name = this.name();
        const req = window.indexedDB.deleteDatabase(name);
        req.onsuccess = () => {
            console.log(`Large deleted successfully ${name}`);
        };

        req.onerror = () => {
            console.log(`Could not delete ${name}`);
        };

        req.onblocked = () => {
            console.log(
                `Couldn't delete database due to the operation being blocked ${name}`
            );
        };
    }

    clear() {
        this.instance.clear();
    }

    numParts(size: number) {
        return Math.ceil(size / LARGE_STORE_FILE_SIZE_LIMIT);
    }

    isLarge(value: string) {
        return !!(value && value.indexOf(LARGE_FILE_MARKER) === 0);
    }

    extract(value: string) {
        const splitParts = value.split("|");

        return {
            marker: splitParts[0] + "|" + splitParts[1],
            path: splitParts[1],
            count: parseInt(splitParts[2]),
        };
    }

    pathPrefix(path: string) {
        return `${LARGE_FILE_MARKER}|${path}`;
    }

    normalPath(path: string, size: number) {
        return `${this.pathPrefix(path)}|${this.numParts(size)}`;
    }

    get(path: string, value: string, resolve: GetResolveFn) {
        const { count } = this.extract(value);
        this._getParts(path, this.pathPrefix(path), count, resolve);
    }

    _getParts(
        _path: string,
        pathPrefix: string,
        count: number,
        resolve: GetResolveFn
    ) {
        const parts: Array<string> = [];
        const next = (counter: number, value: string) => {
            parts.push(value);

            if (counter < count) {
                return this._getPart(pathPrefix, counter, next);
            } else {
                return resolve({ path: _path, value: parts.join("") });
            }
        };

        this._getPart(pathPrefix, 0, next);
    }

    _getPart(
        pathPrefix: string,
        number: number,
        next: (counter: number, value: string) => void
    ) {
        this.instance.getItem(`${pathPrefix}|${number}`, (err, value) => {
            if (err != null) {
                // no need to retrieve more parts we have nothing to retrieve
                return next(Number.MAX_SAFE_INTEGER, "");
            }

            return next(number + 1, (value as unknown) as string);
        });
    }

    set(path: string, value: string, resolve: SetResolveFn) {
        this._setParts(
            path,
            this.pathPrefix(path),
            value,
            this.numParts(value.length),
            resolve
        );
    }

    _setParts(
        _path: string,
        pathPrefix: string,
        value: string,
        count: number,
        resolve: SetResolveFn
    ) {
        const next = (counter: number) => {
            if (counter < count) {
                return this._setPart(pathPrefix, value, counter, next);
            } else {
                return resolve({ path: _path });
            }
        };

        this._setPart(pathPrefix, value, 0, next);
    }

    _setPart(
        pathPrefix: string,
        contents: string,
        number: number,
        next: NextPartFn
    ) {
        const partContents = this._partInContents(contents, number);
        this.instance.setItem(`${pathPrefix}|${number}`, partContents, () =>
            next(number + 1)
        );
    }

    _partInContents(contents: string, number: number) {
        const start = number * LARGE_STORE_FILE_SIZE_LIMIT;
        const end = start + LARGE_STORE_FILE_SIZE_LIMIT;

        if (end > contents.length) {
            return contents.substr(start);
        }

        return contents.substr(start, LARGE_STORE_FILE_SIZE_LIMIT);
    }

    remove(path: string, value: string, resolve: SetResolveFn) {
        const { count } = this.extract(value);
        this._removeParts(path, this.pathPrefix(path), count, resolve);
    }

    _removeParts(
        _path: string,
        pathPrefix: string,
        count: number,
        resolve: SetResolveFn
    ) {
        const next = (counter: number) => {
            if (counter < count) {
                return this._removePart(pathPrefix, counter, next);
            } else {
                return resolve({ path: _path });
            }
        };

        this._removePart(pathPrefix, 0, next);
    }

    _removePart(pathPrefix: string, number: number, next: NextPartFn) {
        this.instance.removeItem(`${pathPrefix}|${number}`, () => {
            return next(number + 1);
        });
    }
}
